import { createFileRoute, Link } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { CtaLink } from "@/components/marketing/CtaLink";
import {
  ArrowRight, Check, Star, Leaf, Plus, Minus,
  CalendarCheck, FileSignature, Radar, Sparkles, Users, ListChecks, KanbanSquare, Video,
} from "lucide-react";
import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { BeforeAfterSlider } from "@/components/marketing/BeforeAfterSlider";
import { useCurrency } from "@/lib/ecom/currency";
import { formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import { getPlanMonthly, formatPlanPrice, defaultPlanPriceText } from "@/components/marketing/pricing-data";
import prodMetal from "@/assets/products/custom-black-metal.webp";
import prodGold from "@/assets/products/custom-gold.webp";
import prodBamboo from "@/assets/products/custom-bamboo.webp";
import logoSvg from "@/assets/banana-logo.svg";
import heroCard from "@/assets/hero-card-new.webp";
import networking from "@/assets/networking-new.png";
import aiAbstract from "/lovable-uploads/11749302-d433-4877-9acb-ac3108b72948.webp";
import phoneCard from "/lovable-uploads/38b31f4c-5833-4861-9072-f959be0b21bb.webp";
import nfcTrio from "@/assets/nfc-card-trio-v2.png";
import nfcTap from "@/assets/nfc-card-tap-new.png";
import nfcLifestyle from "@/assets/nfc-card-lifestyle-new.png";
import nfcThin from "@/assets/nfc-thin.webp";
import floatCardScan from "@/assets/float-card-scan.webp";
import floatProfile from "@/assets/float-profile.webp";
import floatData from "@/assets/float-data.png";

export const Route = createFileRoute("/el/")({
  head: () => ({
    meta: [
      { title: "Ψηφιακές επαγγελματικές κάρτες & πλατφόρμα δικτύωσης AI | Banana Cards" },
      { name: "description", content: "Το Banana Cards είναι η πλατφόρμα δικτύωσης με AI που συνδυάζει ψηφιακές επαγγελματικές κάρτες, κοινή χρήση μέσω NFC, συλλογή δυνητικών πελατών, CRM και έξυπνη διαχείριση επαφών σε ένα μέρος." },
      { property: "og:title", content: "Ψηφιακές επαγγελματικές κάρτες & πλατφόρμα δικτύωσης AI | Banana Cards" },
      { property: "og:description", content: "Το Banana Cards είναι η πλατφόρμα δικτύωσης με AI που συνδυάζει ψηφιακές επαγγελματικές κάρτες, κοινή χρήση μέσω NFC, συλλογή δυνητικών πελατών, CRM και έξυπνη διαχείριση επαφών σε ένα μέρος." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://banana.cards/el/" },
      { property: "og:locale", content: "el_GR" },
      { property: "og:locale:alternate", content: "vi_VN" },
    ],
    links: [
      ...alternateLinkList("/", "el"),
      { rel: "preload", as: "image", href: heroCard, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Banana Cards",
          url: "https://banana.cards/el/",
          logo: "https://banana.cards/el/favicon.ico",
          sameAs: [
            "https://app.banana.cards",
            "https://shop.banana.cards",
          ],
          description: "Πλατφόρμα δικτύωσης με AI που συνδυάζει ψηφιακές επαγγελματικές κάρτες NFC, συλλογή δυνητικών πελατών, CRM, κρατήσεις, συμβάσεις και έξυπνο εμπλουτισμό επαφών.",
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "υποστήριξη πελατών",
              email: "hello@banana.cards",
              availableLanguage: ["Αγγλικά", "Βιετναμικά"],
              areaServed: "Παγκοσμίως",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Banana Cards",
          url: "https://banana.cards/el/",
          inLanguage: ["en", "vi"],
          publisher: { "@type": "Organization", name: "Banana Cards", url: "https://banana.cards/el/" },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://banana.cards/el/help?q={search_term_string}",
            },
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Πλατφόρμα Banana Cards",
          url: "https://banana.cards/el/platform",
          operatingSystem: "Web, iOS, Android",
          applicationCategory: "BusinessApplication",
          applicationSubCategory: "CRM",
          description: "CRM με AI που συνδυάζει επαγγελματικές κάρτες NFC, συλλογή δυνητικών πελατών, επαφές, ροές πωλήσεων, κρατήσεις, συμβάσεις, αξιολογήσεις και αυτοματισμούς σε έναν χώρο εργασίας.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            category: "Freemium",
          },
          featureList: [
            "Ψηφιακές επαγγελματικές κάρτες NFC",
            "Συλλογή δυνητικών πελατών και εμπλουτισμός επαφών",
            "Ροές πωλήσεων και εργασίες",
            "Κρατήσεις και συναντήσεις",
            "Συμβάσεις με ηλεκτρονική υπογραφή",
            "Αυτοματοποιημένα αιτήματα αξιολόγησης",
            "Σύνταξη κειμένων με AI και φωνητικός βοηθός",
            "Κάρτες Apple Wallet και Google Wallet",
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "1280",
            bestRating: "5",
            worstRating: "1",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Επαγγελματικές κάρτες NFC Banana",
          numberOfItems: 6,
          itemListElement: [
            { slug: "metal", name: "Εξατομικευμένη μεταλλική επαγγελματική κάρτα AI", price: 72 },
            { slug: "24kt-gold", name: "Εξατομικευμένη χρυσή επαγγελματική κάρτα AI 24kt", price: 108 },
            { slug: "bamboo", name: "Έξυπνη επαγγελματική κάρτα από μπαμπού", price: 60 },
            { slug: "white-pvc", name: "Εξατομικευμένη λευκή επαγγελματική κάρτα AI από PVC", price: 54 },
            { slug: "black-pvc", name: "Εξατομικευμένη μαύρη επαγγελματική κάρτα AI από PVC", price: 54 },
            { slug: "google-review", name: "Κάρτες Google Review", price: 30 },
          ].map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://banana.cards/el/shop/${p.slug}`,
            item: {
              "@type": "Product",
              name: p.name,
              url: `https://banana.cards/el/shop/${p.slug}`,
              brand: { "@type": "Brand", name: "Banana Cards" },
              category: "Επαγγελματικές κάρτες NFC",
              offers: {
                "@type": "Offer",
                price: p.price,
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: `https://banana.cards/el/shop/${p.slug}`,
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Πώς λειτουργεί μια κάρτα NFC Banana;", acceptedAnswer: { "@type": "Answer", text: "Απλώς αγγίξτε με την κάρτα σας οποιοδήποτε σύγχρονο smartphone — iPhone ή Android. Ολόκληρο το ψηφιακό σας προφίλ ανοίγει αμέσως στο πρόγραμμα περιήγησης του παραλήπτη. Δεν απαιτείται εφαρμογή για καμία πλευρά." } },
            { "@type": "Question", name: "Χρειάζομαι εφαρμογή για να τη χρησιμοποιήσω;", acceptedAnswer: { "@type": "Answer", text: "Όχι. Το άτομο που λαμβάνει την κάρτα σας δεν χρειάζεται να εγκαταστήσει τίποτα. Ως κάτοχος της κάρτας, διαχειρίζεστε το προφίλ σας από τον διαδικτυακό πίνακα ελέγχου ή τη συνοδευτική εφαρμογή μας για κινητά — εσείς επιλέγετε." } },
            { "@type": "Question", name: "Μπορώ να ενημερώσω τα στοιχεία μου μετά την εκτύπωση της κάρτας;", acceptedAnswer: { "@type": "Answer", text: "Ναι — αυτός ακριβώς είναι ο σκοπός. Η φυσική σας κάρτα συνδέεται με το ζωντανό ψηφιακό σας προφίλ. Αλλάξτε θέση εργασίας, αριθμό, συνδέσμους ή σχέδιο οποτεδήποτε και όλα ενημερώνονται αμέσως παντού." } },
            { "@type": "Question", name: "Θα λειτουργεί με το τηλέφωνό μου;", acceptedAnswer: { "@type": "Answer", text: "Οι κάρτες Banana λειτουργούν με σχεδόν κάθε smartphone που κατασκευάστηκε τα τελευταία 7+ χρόνια. Σε iPhone XR και νεότερα μοντέλα ανοίγουν με ένα άγγιγμα, ενώ κάθε τηλέφωνο μπορεί να σαρώσει τον ενσωματωμένο κωδικό QR." } },
            { "@type": "Question", name: "Μπορώ να παραγγείλω κάρτες για όλη την ομάδα μου;", acceptedAnswer: { "@type": "Answer", text: "Βεβαίως. Το πρόγραμμα Ομάδες σάς επιτρέπει να διαθέτετε κάρτες μαζικά, να ελέγχετε κεντρικά την εταιρική ταυτότητα και να διαχειρίζεστε το προφίλ κάθε εργαζομένου από έναν πίνακα διαχείρισης." } },
            { "@type": "Question", name: "Τι περιλαμβάνει το δωρεάν πρόγραμμα;", acceptedAnswer: { "@type": "Answer", text: "Μια ολοκληρωμένη ψηφιακή επαγγελματική κάρτα με κοινή χρήση μέσω QR + συνδέσμου, υποστήριξη Apple & Google Wallet, βασικά αναλυτικά στοιχεία και απεριόριστες αποθηκεύσεις επαφών. Δεν απαιτείται πιστωτική κάρτα για να ξεκινήσετε." } },
            { "@type": "Question", name: "Κάνετε αποστολές στο εξωτερικό;", acceptedAnswer: { "@type": "Answer", text: "Ναι — αποστέλλουμε σε 100+ χώρες με παρακολούθηση παράδοσης. Το κόστος και ο χρόνος αποστολής εμφανίζονται στο ταμείο ανάλογα με τον προορισμό σας." } },
            { "@type": "Question", name: "Ποια είναι η πολιτική επιστροφών σας;", acceptedAnswer: { "@type": "Answer", text: "Αν δεν είστε ικανοποιημένοι με την κάρτα σας, επικοινωνήστε μαζί μας εντός 30 ημερών για δωρεάν αντικατάσταση ή επιστροφή χρημάτων. Οι κάρτες με εξατομικευμένη εκτύπωση καλύπτονται για κατασκευαστικά ελαττώματα." } },
          ],
        }),
      },
    ],
  }),
  component: IndexPage,
});

import { TRUSTED_LOGOS } from "@/assets/logos";

const PRODUCTS = [
  {
    title: "Επαγγελματικές κάρτες NFC",
    desc: "Αγγίξτε οποιοδήποτε τηλέφωνο. Μοιραστείτε αμέσως ολόκληρο το ψηφιακό σας προφίλ — χωρίς εφαρμογή για τον παραλήπτη. Σε μέταλλο, ματ φινίρισμα ή μπαμπού.",
    img: nfcTrio,
    bullets: ["Κοινή χρήση με ένα άγγιγμα, χωρίς εφαρμογή", "Φινιρίσματα μετάλλου, ματ & μπαμπού", "Εξατομικευμένο λογότυπο & σχέδιο"],
    href: "/shop",
    cta: "Αγοράστε κάρτες NFC",
  },
  {
    title: "Εικονικές κάρτες",
    desc: "Το πλήρως ψηφιακό προφίλ σας — μοιραστείτε το μέσω κωδικού QR, συνδέσμου ή Apple/Google Wallet. Πάντα ενημερωμένο. Πάντα μαζί σας.",
    img: phoneCard,
    bullets: ["QR, σύνδεσμος, Apple & Google Wallet", "Ενημέρωση οποτεδήποτε, άμεσα", "Χωρίς φυσική κάρτα"],
    href: "/platform/cards",
    cta: "Δημιουργήστε την κάρτα σας",
  },
  {
    title: "Εφαρμογές Banana",
    desc: "Προσθέστε κρατήσεις, συμβάσεις, αξιολογήσεις και εργαλεία AI στο προφίλ σας. Μία πλατφόρμα, κάθε ροή εργασίας.",
    img: aiAbstract,
    bullets: ["Κρατήσεις, συμβάσεις, αξιολογήσεις", "Επακόλουθη επικοινωνία & εμπλουτισμός με AI", "50+ ενσωματωμένες διασυνδέσεις"],
    href: "/platform/bookings",
    cta: "Εξερευνήστε την πλατφόρμα",
  },
];

const CARD_VARIANTS: { slug: string; name: string; desc: string; img: string; href: string }[] = [
  { slug: "metal",     name: "Εξατομικευμένη μεταλλική κάρτα AI",  desc: "Κορυφαία μεταλλική κάρτα NFC με αντοχή στον χρόνο — με τη δύναμη του AI.",        img: prodMetal,  href: "/shop/metal" },
  { slug: "24kt-gold", name: "Χρυσή κάρτα AI 24kt",     desc: "Πολυτελής επιχρυσωμένη κάρτα NFC 24kt — σχεδιασμένη να εντυπωσιάζει.",       img: prodGold,   href: "/shop/24kt-gold" },
  { slug: "bamboo",    name: "Κάρτα AI από μπαμπού",        desc: "Βιώσιμη κάρτα NFC από μπαμπού — έξυπνη, φυσική, αξέχαστη.",  img: prodBamboo, href: "/shop/bamboo" },
];

const PLATFORM_FEATURES = [
  {
    eyebrow: "Ένα εντυπωσιακό ψηφιακό προφίλ.",
    italic: "Πάντα διαθέσιμο.",
    desc: "Η κάρτα σας βρίσκεται σε έναν μόνιμο σύνδεσμο — με την εταιρική σας ταυτότητα, άμεσα διαθέσιμη για κοινή χρήση και πάντα με τα πιο πρόσφατα στοιχεία σας. Χωρίς εφαρμογή για προβολή ή αποθήκευση.",
    chips: ["Κωδικός QR", "Apple Wallet", "Google Wallet", "Άγγιγμα NFC", "Κοινή χρήση συνδέσμου"],
    img: phoneCard,
    href: "/platform/cards",
    cta: "Ψηφιακές επαγγελματικές κάρτες",
  },
  {
    eyebrow: "Μη χάνετε καμία επαφή",
    italic: "ποτέ ξανά.",
    desc: "Κάθε άτομο που σαρώνει την κάρτα σας καταγράφεται αυτόματα. Προσθέστε σημειώσεις, ετικέτες και υπενθυμίσεις επικοινωνίας. Δείτε ποιος προέβαλε την κάρτα σας και πότε.",
    chips: ["Αυτόματη καταγραφή", "Σημειώσεις & ετικέτες", "Επακόλουθη επικοινωνία", "Αναλυτικά στοιχεία"],
    img: networking,
    href: "/platform/contacts",
    cta: "Επαφές",
  },
  {
    eyebrow: "Διαχειριστείτε τις κάρτες της ομάδας σας",
    italic: "από ένα μέρος.",
    desc: "Δημιουργήστε κάρτες για όλη την ομάδα σας σε λίγα λεπτά. Ορίστε κατευθυντήριες γραμμές εταιρικής ταυτότητας, ενημερώστε κεντρικά τα στοιχεία επικοινωνίας και παρακολουθήστε την αλληλεπίδραση σε ολόκληρο τον οργανισμό σας.",
    chips: ["Πίνακας ελέγχου ομάδας", "Μαζική διάθεση καρτών", "Έλεγχος εταιρικής ταυτότητας", "Διαχείριση"],
    img: heroCard,
    href: "/teams",
    cta: "Ομάδες",
  },
  {
    eyebrow: "Συνδέεται με τα εργαλεία",
    italic: "που ήδη χρησιμοποιείτε.",
    desc: "Στείλτε νέες επαφές απευθείας στο CRM σας. Συγχρονίστε με HubSpot, Salesforce, Pipedrive και άλλα — χωρίς χειροκίνητη εισαγωγή δεδομένων.",
    chips: ["HubSpot", "Salesforce", "Outlook", "Zapier", "Pipedrive", "Slack"],
    img: aiAbstract,
    href: "/platform/integrations",
    cta: "Διασυνδέσεις",
  },
  {
    eyebrow: "Μάθετε ακριβώς πώς η κάρτα σας",
    italic: "αποδίδει.",
    desc: "Παρακολουθήστε προβολές, αποθηκεύσεις και κλικ σε συνδέσμους σε πραγματικό χρόνο. Μάθετε ποιες επαφές αλληλεπιδρούν και βελτιστοποιήστε την απόδοση της επένδυσής σας στη δικτύωση.",
    chips: ["Προβολές κάρτας", "Αποθηκεύσεις", "Κλικ σε συνδέσμους", "Αναλυτικά στοιχεία ομάδας"],
    img: nfcLifestyle,
    href: "/platform/analytics",
    cta: "Αναλυτικά στοιχεία",
  },
];

const STEPS = [
  { tag: "Ρύθμιση",   title: "Δημιουργήστε την κάρτα σας",  desc: "Δημιουργήστε το ψηφιακό σας προφίλ σε λίγα λεπτά. Προσθέστε φωτογραφία, συνδέσμους και εταιρική ταυτότητα. Εξατομικεύστε το σχέδιο της κάρτας NFC σας αν παραγγείλετε φυσική κάρτα." },
  { tag: "Κοινή χρήση",   title: "Συνδεθείτε παντού", desc: "Αγγίξτε με την κάρτα NFC σας, δείξτε τον κωδικό QR σας ή στείλτε τον σύνδεσμό σας. Οι παραλήπτες βλέπουν αμέσως ολόκληρο το προφίλ σας — χωρίς εφαρμογή, χωρίς εμπόδια." },
  { tag: "Διαχείριση",  title: "Αναπτύξτε το δίκτυό σας",desc: "Κάθε νέα γνωριμία αποθηκεύεται και οργανώνεται αυτόματα. Επικοινωνήστε ξανά πιο έξυπνα, συγχρονίστε με το CRM σας και μη χάσετε ποτέ το ενδιαφέρον ενός δυνητικού πελάτη." },
];

const PERSONAS = [
  { title: "Για στελέχη & ιδρυτές",      desc: "Κάντε κάθε πρώτη εντύπωση να μετράει. Η κάρτα σας αντανακλά την ποιότητα της εταιρικής σας ταυτότητας — κορυφαία, προσεγμένη, πάντα επίκαιρη.", img: heroCard,     href: "/solutions/founders", cta: "Εξερευνήστε λύσεις για ιδρυτές" },
  { title: "Για πωλήσεις & επιχειρηματική ανάπτυξη",    desc: "Κλείστε περισσότερες συμφωνίες. Καταγράψτε κάθε δυνητικό πελάτη. Συγχρονίστε αυτόματα τις επαφές στο CRM σας και επικοινωνήστε ξανά πριν από τον ανταγωνισμό.",     img: networking,   href: "/solutions/sales",    cta: "Δείτε λειτουργίες πωλήσεων" },
  { title: "Για δημιουργούς & ελεύθερους επαγγελματίες", desc: "Ξεχωρίστε από το πλήθος. Η ψηφιακή σας κάρτα είναι ένα μικρό χαρτοφυλάκιο — προσθέστε τον ιστότοπο, τα κοινωνικά δίκτυα, βίντεο και δείγματα της δουλειάς σας.",          img: phoneCard,    href: "/solutions/founders", cta: "Δείτε λειτουργίες για ελεύθερους επαγγελματίες" },
];

const SPECS = [
  { v: "0.8mm",       l: "λεπτή",          d: "Λεπτότερη από μια συνηθισμένη πιστωτική κάρτα" },
  { v: "NFC + QR",    l: "δύο σε ένα",    d: "Δύο τρόποι κοινής χρήσης σε μία κάρτα" },
  { v: "Κάθε τηλέφωνο",   l: "συμβατότητα", d: "iPhone & Android, χωρίς εφαρμογή" },
  { v: "Εξατομικευμένο",      l: "σχέδιο",        d: "Εκτύπωση έως την άκρη, χάραξη ή ανάγλυφη αποτύπωση" },
  { v: "Άμεσες",     l: "ενημερώσεις",       d: "Αλλάξτε το ψηφιακό σας προφίλ οποτεδήποτε" },
  { v: "Ανθεκτική",     l: "κατασκευή",         d: "Αδιάβροχη, ανθεκτική στις γρατζουνιές" },
];

const STATS = [
  { v: "200k+", l: "κάρτες παραδόθηκαν παγκοσμίως" },
  { v: "100+",  l: "χώρες αποστολής" },
  { v: "4.9",   l: "μέση βαθμολογία" },
  { v: "50k+",  l: "ενεργοί χρήστες" },
];

const REVIEWS = [
  { q: "Το banana.cards άλλαξε εντελώς τον τρόπο δικτύωσης της ομάδας πωλήσεών μας. Κάθε δυνητικός πελάτης καταγράφεται αυτόματα και μεταφέρεται στο Salesforce. Καμία χειροκίνητη εργασία.", a: "James T.", r: "Αντιπρόεδρος Πωλήσεων, Finvi" },
  { q: "Η κάρτα NFC είναι εντυπωσιακή — όλοι με ρωτούν γι’ αυτή στις εκδηλώσεις. Η δυνατότητα να ενημερώνω τα στοιχεία μου οποτεδήποτε χωρίς επανεκτύπωση αλλάζει τα πάντα.", a: "Sarah M.", r: "Ιδρύτρια" },
  { q: "Το διαθέσαμε σε 200 εργαζομένους μέσα σε ένα απόγευμα. Ο πίνακας ελέγχου ομάδας κάνει την κεντρική διαχείριση όλων των καρτών απίστευτα εύκολη.", a: "Mark R.",  r: "Διευθυντής Πληροφορικής" },
  { q: "Μου αρέσει που οι παραλήπτες δεν χρειάζεται να κατεβάσουν τίποτα. Ένα άγγιγμα ή μια σάρωση και αμέσως — η επαφή μου βρίσκεται στο τηλέφωνό τους.",                       a: "Lucas P.", r: "Μεσίτης ακινήτων" },
  { q: "Επιτέλους μια επαγγελματική κάρτα αντάξια της ποιότητας της επωνυμίας μας. Η μεταλλική κάρτα με χαραγμένο το λογότυπό μας είναι εκπληκτική.",                                     a: "Elena K.", r: "Δημιουργική διευθύντρια" },
  { q: "Τα αναλυτικά στοιχεία είναι εξαιρετικά — βλέπω ακριβώς ποιος προέβαλε την κάρτα μου μετά από κάθε εκδήλωση και επικοινωνώ ξανά όσο η συζήτηση είναι ακόμη πρόσφατη.",               a: "David W.", r: "Στέλεχος πωλήσεων" },
];

const REVIEWS_EXTRA = [
  { q: "Ως υπεύθυνη διαχείρισης πελατών, αυτό το εργαλείο με βοηθά να διαχειρίζομαι επαφές για εμένα, την ομάδα μου και όχι μόνο. Η κάρτα βελτίωσε σημαντικά την αποδοτικότητά μας.", a: "Anna-Rose H.", r: "Υπεύθυνη διαχείρισης πελατών" },
  { q: "Τέλος στις χαμένες επαγγελματικές κάρτες των άλλων και στις δικές μου που τελειώνουν για να τις χάσουν κι εκείνοι. Το λατρεύω!", a: "Allen P.", r: "Σύμβουλος επαγγελματικής ανάπτυξης" },
  { q: "Προστατεύουμε τα δέντρα και η λειτουργία με άγγιγμα είναι εξαιρετική για τα νεότερα τηλέφωνα.", a: "Max R.", r: "Πωλητής" },
  { q: "Μου αρέσουν πολύ η απλότητα, η επιλογή υλικών και ο κομψός σχεδιασμός. Το περιβάλλον της εφαρμογής είναι ευχάριστο στη χρήση.", a: "Angelica A.", r: "Συνιδιοκτήτρια" },
  { q: "Είναι πολύ εύκολο να ανταλλάσσετε στοιχεία με όποιον γνωρίζετε. Δεν χρειάζεστε τυπωμένες επαγγελματικές κάρτες. Τα στοιχεία πηγαίνουν απευθείας στις επαφές σας.", a: "Ozana G.", r: "Επικεφαλής μάρκετινγκ" },
  { q: "Η δυνατότητα εξατομίκευσης χρωμάτων, γραμματοσειρών και διατάξεων αποτυπώνει τέλεια την εταιρική σας ταυτότητα ή το στιλ σας.", a: "Tatiana E.", r: "Ταξιδιωτική πράκτορας" },
  { q: "Ο σχεδιασμός, η απλή ρύθμιση του προφίλ και η ευκολία να μοιράζομαι τα πάντα με ένα άγγιγμα της κάρτας μου — απίστευτα.", a: "Brandon R.", r: "Επιθεωρητής βιομηχανικών εγκαταστάσεων" },
  { q: "Προσωπικά εκτιμώ την ευκολία και την ασφάλεια της κοινής χρήσης ψηφιακών καρτών. Δεν χρειάζεται να κουβαλάω επιπλέον επαγγελματικές κάρτες.", a: "Priya N.", r: "Σύμβουλος" },
  { q: "Βιώσιμες, όμορφες και τις χρησιμοποιεί όλη η διοικητική μας ομάδα. Μεγάλη αναβάθμιση σε σχέση με το χαρτί.", a: "Diego F.", r: "Διευθυντής λειτουργιών" },
];

const FAQS = [
  { q: "Πώς λειτουργεί μια κάρτα NFC Banana;", a: "Απλώς αγγίξτε με την κάρτα σας οποιοδήποτε σύγχρονο smartphone — iPhone ή Android. Ολόκληρο το ψηφιακό σας προφίλ ανοίγει αμέσως στο πρόγραμμα περιήγησης του παραλήπτη. Δεν απαιτείται εφαρμογή για καμία πλευρά." },
  { q: "Χρειάζομαι εφαρμογή για να τη χρησιμοποιήσω;", a: "Όχι. Το άτομο που λαμβάνει την κάρτα σας δεν χρειάζεται να εγκαταστήσει τίποτα. Ως κάτοχος της κάρτας, διαχειρίζεστε το προφίλ σας από τον διαδικτυακό πίνακα ελέγχου ή τη συνοδευτική εφαρμογή μας για κινητά — εσείς επιλέγετε." },
  { q: "Μπορώ να ενημερώσω τα στοιχεία μου μετά την εκτύπωση της κάρτας;", a: "Ναι — αυτός ακριβώς είναι ο σκοπός. Η φυσική σας κάρτα συνδέεται με το ζωντανό ψηφιακό σας προφίλ. Αλλάξτε θέση εργασίας, αριθμό, συνδέσμους ή σχέδιο οποτεδήποτε και όλα ενημερώνονται αμέσως παντού." },
  { q: "Θα λειτουργεί με το τηλέφωνό μου;", a: "Οι κάρτες Banana λειτουργούν με σχεδόν κάθε smartphone που κατασκευάστηκε τα τελευταία 7+ χρόνια. Σε iPhone XR και νεότερα μοντέλα ανοίγουν με ένα άγγιγμα, ενώ κάθε τηλέφωνο μπορεί να σαρώσει τον ενσωματωμένο κωδικό QR." },
  { q: "Μπορώ να παραγγείλω κάρτες για όλη την ομάδα μου;", a: "Βεβαίως. Το πρόγραμμα Ομάδες σάς επιτρέπει να διαθέτετε κάρτες μαζικά, να ελέγχετε κεντρικά την εταιρική ταυτότητα και να διαχειρίζεστε το προφίλ κάθε εργαζομένου από έναν πίνακα διαχείρισης." },
  { q: "Τι περιλαμβάνει το δωρεάν πρόγραμμα;", a: "Μια ολοκληρωμένη ψηφιακή επαγγελματική κάρτα με κοινή χρήση μέσω QR + συνδέσμου, υποστήριξη Apple & Google Wallet, βασικά αναλυτικά στοιχεία και απεριόριστες αποθηκεύσεις επαφών. Δεν απαιτείται πιστωτική κάρτα για να ξεκινήσετε." },
  { q: "Κάνετε αποστολές στο εξωτερικό;", a: "Ναι — αποστέλλουμε σε 100+ χώρες με παρακολούθηση παράδοσης. Το κόστος και ο χρόνος αποστολής εμφανίζονται στο ταμείο ανάλογα με τον προορισμό σας." },
  { q: "Ποια είναι η πολιτική επιστροφών σας;", a: "Αν δεν είστε ικανοποιημένοι με την κάρτα σας, επικοινωνήστε μαζί μας εντός 30 ημερών για δωρεάν αντικατάσταση ή επιστροφή χρημάτων. Οι κάρτες με εξατομικευμένη εκτύπωση καλύπτονται για κατασκευαστικά ελαττώματα." },
];

const BLOGS = [
  { title: "Τι είναι η ψηφιακή επαγγελματική κάρτα; (Πλήρης οδηγός 2026)", excerpt: "Ανακαλύψτε τι είναι οι ψηφιακές επαγγελματικές κάρτες, πώς λειτουργούν και γιατί το 88% των επαγγελματιών τις προτιμά αντί για χάρτινες.", img: phoneCard, href: "/about" },
  { title: "Οι 8 καλύτερες ψηφιακές επαγγελματικές κάρτες του 2026 (Κατάταξη & αξιολόγηση)", excerpt: "Αξιολογήσαμε και δοκιμάσαμε τις 8 καλύτερες πλατφόρμες ψηφιακών επαγγελματικών καρτών — τιμές, δωρεάν προγράμματα και λειτουργίες.", img: networking, href: "/about" },
  { title: "Πώς να δημιουργήσετε μια ψηφιακή επαγγελματική κάρτα σε 5 λεπτά", excerpt: "Δημιουργήστε μια επαγγελματική ψηφιακή κάρτα σε 5 λεπτά. Χωρίς εφαρμογή, με δωρεάν Apple Wallet και λειτουργία σε κάθε συσκευή.", img: nfcTrio, href: "/about" },
  { title: "NFC ή QR: Ποιος τρόπος κοινής χρήσης κερδίζει το 2026;", excerpt: "Μια αναλυτική σύγκριση των δύο δημοφιλέστερων τρόπων κοινής χρήσης στοιχείων επικοινωνίας — και γιατί χρησιμοποιούμε και τους δύο.", img: nfcTap, href: "/about" },
  { title: "Γιατί η κατάργηση του χαρτιού ενισχύει τη βιωσιμότητα", excerpt: "Οι χάρτινες επαγγελματικές κάρτες κοστίζουν στον πλανήτη 7 εκατομμύρια δέντρα τον χρόνο. Δείτε πώς οι ψηφιακές κάρτες το αλλάζουν αυτό.", img: aiAbstract, href: "/about" },
  { title: "Σχεδιάζοντας κάρτες που μένουν αξέχαστες", excerpt: "Επιλογές χρωμάτων, υλικών και τυπογραφίας που κάνουν τη φυσική σας κάρτα NFC αξέχαστη στις εκδηλώσεις.", img: nfcLifestyle, href: "/about" },
];

type HomePlan = {
  name: string;
  planKey: "free" | "pro_ai" | "teams";
  desc: string;
  cta: string;
  href: string;
  features: string[];
  featured?: boolean;
};

const PLANS: HomePlan[] = [
  {
    name: "Δωρεάν",
    planKey: "free",
    desc: "Ιδανικό για όσους κάνουν τα πρώτα τους βήματα.",
    cta: "Ξεκινήστε δωρεάν",
    href: "/signup",
    features: ["1 ψηφιακή κάρτα", "Κοινή χρήση μέσω QR & συνδέσμου", "Apple & Google Wallet", "Βασικά αναλυτικά στοιχεία", "Απεριόριστες αποθηκεύσεις επαφών"],
  },
  {
    name: "Pro AI",
    planKey: "pro_ai",
    desc: "Για επαγγελματίες που επενδύουν στη δικτύωση.",
    cta: "Ξεκινήστε το Pro δωρεάν",
    href: "/signup",
    features: ["Όλα όσα περιλαμβάνει το Δωρεάν", "Εξατομικευμένο σχέδιο κάρτας", "Προηγμένα αναλυτικά στοιχεία", "Διασυνδέσεις CRM", "Υπενθυμίσεις επακόλουθης επικοινωνίας", "Υποστήριξη κατά προτεραιότητα"],
    featured: true,
  },
  {
    name: "Ομάδες",
    planKey: "teams",
    desc: "Για ομάδες 5 ή περισσότερων ατόμων.",
    cta: "Κλείστε μια επίδειξη",
    href: "/contact",
    features: ["Όλα όσα περιλαμβάνει το Pro", "Πίνακας ελέγχου & διαχείριση ομάδας", "Μαζική δημιουργία καρτών", "Έλεγχοι εταιρικής ταυτότητας & δικαιωμάτων", "SSO & εταιρικές διασυνδέσεις", "Αποκλειστικός υπεύθυνος λογαριασμού"],
  },
];

function IndexPage() {
  const { country } = useCurrency();
  const heroVideoRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroVideoRef,
    offset: ["start end", "end start"],
  });
  const heroScale = useTransform(heroScroll, [0, 0.45, 1], [0.94, 1.04, 1.08]);

  const supersectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: superScroll } = useScroll({
    target: supersectionRef,
    offset: ["start 85%", "center center"],
  });
  // Images fly outward as user scrolls; video grows. At progress 1 (midpoint
  // of section in viewport) images are fully out and video is full size.
  // Linger 10% longer before flying out — start outward motion at 0.1
  const floatLeftX = useTransform(superScroll, [0.1, 1], [0, -520]);
  const floatLeftY = useTransform(superScroll, [0.1, 1], [0, 140]);
  const floatLeftR = useTransform(superScroll, [0.1, 1], [-6, -18]);

  const floatRightX = useTransform(superScroll, [0.1, 1], [0, 520]);
  const floatRightY = useTransform(superScroll, [0.1, 1], [0, 120]);
  const floatRightR = useTransform(superScroll, [0.1, 1], [6, 18]);

  const floatBottomX = useTransform(superScroll, [0.1, 1], [0, -120]);
  const floatBottomY = useTransform(superScroll, [0.1, 1], [0, 360]);
  const floatBottomR = useTransform(superScroll, [0.1, 1], [-3, -14]);

  const floatOpacity = useTransform(superScroll, [0.1, 0.9, 1], [1, 0.2, 0]);
  const videoScale = useTransform(superScroll, [0, 1], [0.82, 1.02]);

  // Mute toggles for both videos
  const heroVideoEl = useRef<HTMLVideoElement>(null);
  const featuresVideoEl = useRef<HTMLVideoElement>(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const [featuresMuted, setFeaturesMuted] = useState(true);
  const toggleMute = (
    el: React.RefObject<HTMLVideoElement | null>,
    muted: boolean,
    setMuted: (v: boolean) => void,
  ) => {
    const next = !muted;
    setMuted(next);
    if (el.current) {
      el.current.muted = next;
      if (!next) {
        el.current.play().catch(() => {});
      }
    }
  };

  return (
    <SiteLayout>
      {/* SUPERCHARGED — white section with floating images around a video */}
      <section className="relative overflow-hidden bg-background">
        <div className="mx-auto max-w-[1400px] px-4 py-20 md:px-8 md:py-36">
          <div className="mx-auto max-w-3xl text-left md:text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground border border-foreground/10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              Η ΥΠΕΡΕΦΑΡΜΟΓΗ ΔΙΚΤΥΩΣΗΣ
            </span>
            <h2 className="mt-5 font-display text-3xl font-bold leading-[1.1] md:whitespace-nowrap md:text-5xl">
              Η επιχείρησή σας.{" "}
              <span className="relative inline-block align-baseline">
                <svg
                  className="sc-svg"
                  viewBox="0 0 740 100"
                  preserveAspectRatio="xMinYMid meet"
                  aria-hidden="true"
                  focusable="false"
                >
                  <defs>
                    <linearGradient id="sc-grad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#fbbf77" />
                      <stop offset="20%" stopColor="#f59e88" />
                      <stop offset="40%" stopColor="#f06c8a" />
                      <stop offset="55%" stopColor="#ec4899" />
                      <stop offset="72%" stopColor="#c563d6" />
                      <stop offset="88%" stopColor="#b057ee" />
                      <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                  </defs>
                  <text x="0" y="80" className="sc-base">Με νέα δυναμική.</text>
                  <text x="0" y="80" className="sc-fill" fill="url(#sc-grad)">Με νέα δυναμική.</text>
                  <text x="0" y="80" className="sc-trail" stroke="url(#sc-grad)">Με νέα δυναμική.</text>
                </svg>
                <span className="sr-only">Με νέα δυναμική.</span>
              </span>
            </h2>
            <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground md:mx-auto md:text-lg">
              Τα banana.cards είναι έξυπνα εργαλεία με AI που βοηθούν την επιχείρησή σας να αναπτυχθεί. Μοιραστείτε το
              ψηφιακό σας προφίλ με ένα άγγιγμα και αποκτήστε ένα οικοσύστημα που παρακολουθεί δυνητικούς πελάτες, συντάσσει μηνύματα
              και αυτοματοποιεί τη ροή εργασίας σας — η επωνυμία, τα δεδομένα και η επιχείρησή σας, όλα συνδεδεμένα.
            </p>
            <div className="mt-8 flex w-full md:w-auto md:justify-center">
              <a href="https://app.banana.cards" target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-dark px-7 py-3.5 text-sm font-bold text-background transition hover:opacity-90 md:inline-flex md:w-auto">
                Ξεκινήστε δωρεάν <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div ref={supersectionRef} className="relative mx-auto mt-12 max-w-5xl md:mt-20">
            {/* Floating card scan — top-left */}
            <motion.img
              src={floatCardScan}
              alt="Σαρωτής επαγγελματικών καρτών"
              style={{ x: floatLeftX, y: floatLeftY, rotate: floatLeftR, opacity: floatOpacity }}
              className="pointer-events-none absolute -left-6 -top-16 z-20 hidden w-[230px] rounded-2xl md:block md:w-[300px] lg:-left-20 lg:w-[360px]"
              loading="lazy"
            />
            {/* Floating profile — right */}
            <motion.img
              src={floatProfile}
              alt="Προφίλ ψηφιακής επαγγελματικής κάρτας"
              style={{ x: floatRightX, y: floatRightY, rotate: floatRightR, opacity: floatOpacity }}
              className="pointer-events-none absolute -right-4 top-12 z-20 hidden w-[180px] rounded-3xl md:block md:w-[220px] lg:-right-16 lg:w-[260px]"
              loading="lazy"
            />
            {/* Floating data — bottom-left */}
            <motion.img
              src={floatData}
              alt="Πίνακας αναλυτικών στοιχείων"
              style={{ x: floatBottomX, y: floatBottomY, rotate: floatBottomR, opacity: floatOpacity }}
              className="pointer-events-none absolute -bottom-12 left-2 z-20 hidden w-[200px] rounded-2xl md:block md:w-[240px] lg:-left-12 lg:-bottom-20 lg:w-[280px]"
              loading="lazy"
            />

            {/* Video — grows as user scrolls */}
            <motion.div
              style={{ scale: videoScale }}
              className="relative origin-center overflow-hidden rounded-2xl bg-brand-dark"
            >
              <video ref={featuresVideoEl} className="aspect-video w-full object-cover rounded-none opacity-100" autoPlay loop muted playsInline poster={heroCard} preload="none" {...({ fetchpriority: "high" } as Record<string, string>)}>
                <source src="/features-video.webm" type="video/webm" />
                <track kind="captions" />
              </video>
              <button
                type="button"
                onClick={() => toggleMute(featuresVideoEl, featuresMuted, setFeaturesMuted)}
                aria-label={featuresMuted ? "Ενεργοποίηση ήχου βίντεο" : "Σίγαση βίντεο"}
                className="absolute bottom-4 right-4 z-30 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 md:bottom-5 md:right-5"
              >
                {featuresMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* LOGO MARQUEE — Trusted by professionals at */}
      <section className="bg-white">
        <div className="relative pb-20 pt-8 md:pb-28 md:pt-10 border-[#fcfcfc]">
          <p className="mb-5 text-center text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:mb-6">
            Μας εμπιστεύονται επαγγελματίες από
          </p>
          <div className="relative overflow-hidden">
            <div className="animate-marquee flex w-max items-center gap-6 px-6 [mask-image:linear-gradient(to_right,transparent,#000_10%,#000_90%,transparent)] md:gap-10">
              {[...TRUSTED_LOGOS, ...TRUSTED_LOGOS].map((logo, i) => (
                <span
                  key={i}
                  className="inline-flex h-20 w-20 shrink-0 items-center justify-center md:h-24 md:w-24"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    loading="lazy"
                    decoding="async"
                    width={96}
                    height={96}
                    className="h-full w-full object-contain"
                  />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* APPS SHOWCASE — bento grid of platform apps */}
      <section className="relative overflow-hidden bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <div className="mb-12 flex flex-col items-start gap-6 md:mb-16 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-card px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
                CRM ΜΕ ΤΗ ΔΥΝΑΜΗ ΤΟΥ AI
              </span>
              <h2 className="mt-5 font-display text-3xl leading-[1.05] tracking-[-0.01em] md:text-5xl">
                Εφαρμογές που <em className="italic">δίνουν ώθηση στην επιχείρησή σας.</em>
              </h2>
              <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-muted-foreground md:text-lg">
                Κάθε λογαριασμός banana.cards περιλαμβάνει μια συνεχώς αναπτυσσόμενη σουίτα ενσωματωμένων εφαρμογών —
                εγκαταστήστε μόνο ό,τι χρειάζεστε, όλα συνδεδεμένα με τις επαφές σας και το AI.
              </p>
            </div>
            <Link
              to="/platform"
              className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background transition hover:opacity-90"
            >
              Δείτε όλες τις εφαρμογές <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: CalendarCheck, name: "Κρατήσεις", tag: "Προγραμματισμός", desc: "Επιτρέψτε στους δυνητικούς πελάτες να κλείνουν συναντήσεις μόλις αγγίξουν την κάρτα σας. Συγχρονισμός ημερολογίου, υπενθυμίσεις, πληρωμές — όλα ενσωματωμένα.", href: "/platform/bookings", color: "from-[#E8F5D8] to-[#C9E89A]" },
              { icon: FileSignature, name: "Συμβάσεις", tag: "Ηλεκτρονική υπογραφή", desc: "Στείλτε, υπογράψτε και αποθηκεύστε συμβάσεις χωρίς να φύγετε από την εφαρμογή. Νομικά δεσμευτικές, έτοιμες για έλεγχο.", href: "/platform/contracts", color: "from-[#FFE9D6] to-[#FFC79A]" },
              { icon: Video, name: "Συναντήσεις", tag: "Ζωντανές κλήσεις", desc: "Έξυπνες βιντεοσυναντήσεις με σημειώσεις AI, ενέργειες προς υλοποίηση και ενσωματωμένο συγχρονισμό CRM.", href: "/platform/meetings", color: "from-[#D0EEFF] to-[#8FCEFF]" },
              { icon: Users, name: "CRM", tag: "Επαφές", desc: "Κάθε άγγιγμα, σάρωση και δυνητικός πελάτης συγκεντρώνεται εδώ — με ετικέτες, βαθμολόγηση και έτοιμος για το επόμενο βήμα.", href: "/platform/crm", color: "from-[#D6F3EE] to-[#9CDCD0]" },
              { icon: KanbanSquare, name: "Ροές πωλήσεων", tag: "Λειτουργίες πωλήσεων", desc: "Οπτικές ροές συμφωνιών που προχωρούν αυτόματα καθώς αλληλεπιδρούν οι επαφές σας.", href: "/platform/pipelines", color: "from-[#E4E4E7] to-[#B8B8C0]" },
              { icon: ListChecks, name: "Εργασίες", tag: "Ροή εργασίας", desc: "Μετατρέψτε τις συζητήσεις σε εργασίες. Αναθέστε, παρακολουθήστε και ολοκληρώστε — ατομικά ή ως ομάδα.", href: "/platform/tasks", color: "from-[#FFF4C2] to-[#FFE066]" },
              { icon: Radar, name: "Scout", tag: "Αναζήτηση πελατών με AI", desc: "Αφήστε το AI να βρει πελάτες παρόμοιους με τους καλύτερούς σας και να ξεκινήσει την επικοινωνία για εσάς.", href: "/platform/scout", color: "from-[#DCE7FF] to-[#A8C0FF]" },
              { icon: Sparkles, name: "Σουίτα AI", tag: "Βοηθοί AI", desc: "Επακόλουθα μηνύματα, περιλήψεις, εμπλουτισμός και απαντήσεις — γραμμένα για εσάς, με τη δική σας φωνή.", href: "/platform/ai-suite", color: "from-[#EADFFF] to-[#C7AEFF]" },
            ].map((app, i) => {
              const Icon = app.icon;
              const featured = false;
              return (
                <Link
                  key={app.name}
                  to={app.href}
                  className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl bg-card p-6 transition hover:-translate-y-1 ${
                    featured ? "lg:col-span-2 lg:row-span-1" : ""
                  }`}
                >
                  {/* Placeholder image area with gradient */}
                  <div className={`relative mb-5 flex h-32 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br ${app.color}`}>
                    <Icon className="h-12 w-12 text-foreground/70 transition group-hover:scale-110" strokeWidth={1.5} />
                    <span className="absolute left-3 top-3 inline-flex items-center rounded-full bg-background/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-foreground backdrop-blur">
                      {app.tag}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display text-xl leading-tight md:text-2xl">{app.name}</h3>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted transition group-hover:bg-foreground group-hover:text-background">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{app.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[12px] font-medium uppercase tracking-[0.2em] text-muted-foreground md:mt-14">
            <span>+ Διασυνδέσεις</span><span className="text-foreground/20">·</span>
            <span>+ Αναλυτικά στοιχεία</span><span className="text-foreground/20">·</span>
            <span>+ Ψηφιακό πορτοφόλι</span><span className="text-foreground/20">·</span>
            <span>+ Ανταλλαγή δυνητικών πελατών</span><span className="text-foreground/20">·</span>
            <span>νέες εφαρμογές κάθε μήνα</span>
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 max-w-2xl md:mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Σχεδιασμένο για όλους</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Όποιοι κι αν είστε. <em className="italic">Όπως κι αν εργάζεστε.</em>
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {PERSONAS.map((p) => (
            <Link key={p.title} to={p.href} className="group flex flex-col overflow-hidden rounded-2xl bg-card transition">
              <div className="aspect-[5/4] overflow-hidden bg-muted">
                <img src={p.img} alt={p.title} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-display text-xl font-bold leading-tight md:text-2xl">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                  {p.cta} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-10 rounded-3xl bg-card p-7 md:mt-12 md:p-14">
          <div className="grid items-center gap-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="font-display text-2xl leading-tight md:text-4xl">
                Σχεδιασμένο για ομάδες.
              </h3>
              <p className="mt-3 max-w-xl text-sm text-muted-foreground md:text-base">
                Από 5 έως 5.000 εργαζομένους — διαχειριστείτε κάθε κάρτα, εταιρική ταυτότητα και γνωριμία από έναν πίνακα διαχείρισης. Σχεδιασμένο για επιχειρήσεις.
              </p>
            </div>
            <div className="md:text-right">
              <Link to="/teams" className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Εξερευνήστε προγράμματα ομάδων <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* PLATFORM — left sticky intro, right scrolling features */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 md:px-6 md:pb-28 md:pt-24">
          <div className="grid gap-10 md:grid-cols-12 md:gap-12">
            {/* Sticky intro — left column on desktop */}
            <aside className="md:col-span-5 md:sticky md:top-24 self-start">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Πλατφόρμα</p>
              <h2 className="mt-3 font-display text-3xl leading-[1.05] md:text-5xl">
                Η επιχείρησή σας. <em className="italic">Με νέα δυναμική.</em>
              </h2>
              <p className="mt-5 max-w-md text-[15px] text-muted-foreground md:text-base">
                Το banana.cards είναι κάτι περισσότερο από μια κάρτα — είναι η ολοκληρωμένη ψηφιακή σας ταυτότητα και ο κόμβος δικτύωσής σας.
                Σχεδιασμένο για ιδιώτες, ομάδες και επιχειρήσεις.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-dark px-5 py-3 text-sm font-bold text-background transition hover:opacity-90">
                  Ξεκινήστε δωρεάν <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <Link to="/pricing" className="inline-flex items-center justify-center gap-2 rounded-xl bg-card px-5 py-3 text-sm font-bold transition hover:opacity-80">
                  Δείτε τις τιμές
                </Link>
              </div>
              {/* Decorative count chip */}
              <p className="mt-10 hidden text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground md:block">
                <span className="font-display text-2xl text-foreground">{PLATFORM_FEATURES.length}</span>
                <span className="ml-2 align-middle">δυνατότητες · μία πλατφόρμα</span>
              </p>
            </aside>

            {/* Feature list — right column */}
            <div className="md:col-span-7">
              <ol className="space-y-5 md:space-y-6">
                {PLATFORM_FEATURES.map((f, i) => {
                  const total = PLATFORM_FEATURES.length;
                  const num = `0${i + 1}`;
                  return (
                    <li key={f.eyebrow} className="rounded-3xl bg-card p-6 md:p-8">
                      <div className="flex items-center justify-between gap-4">
                        <span className="inline-flex items-center gap-2 rounded-xl bg-foreground px-3 py-1.5 font-display text-sm font-bold tracking-wider text-background">
                          <span className="text-brand-lime">{num}</span>
                          <span className="text-background/60">/ 0{total}</span>
                        </span>
                        <Link to={f.href} className="inline-flex items-center gap-1 text-xs font-bold underline-offset-4 hover:underline">
                          {f.cta} <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </div>
                      <h3 className="mt-5 font-display text-2xl leading-[1.15] md:text-3xl">
                        {f.eyebrow} <em className="italic">{f.italic}</em>
                      </h3>
                      <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-muted-foreground md:text-[15px]">
                        {f.desc}
                      </p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {f.chips.map((c) => (
                          <span key={c} className="inline-flex items-center rounded-full bg-muted px-3 py-1.5 text-[12px] font-semibold border">
                            {c}
                          </span>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* STATS — matches section above (muted), white box inside */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
          <div className="grid grid-cols-2 gap-6 rounded-3xl bg-background p-8 md:grid-cols-4 md:gap-8 md:p-12">
            {STATS.map((s) => (
              <div key={s.l}>
                <p className="font-display text-4xl leading-none md:text-6xl">{s.v}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HERO — Change the way you connect */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-white md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-[1600px] px-4 pb-14 pt-10 md:px-8 md:pb-20 md:pt-24 2xl:px-12">
          <div className="mx-auto flex max-w-5xl flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-foreground border border-foreground/10">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-lime" />
              Το μέλλον της δικτύωσης
            </span>
            <h1 className="mt-5 font-display text-4xl leading-[1.05] tracking-[-0.02em] md:text-6xl md:leading-[1.02]">
              Αλλάξτε τον τρόπο που συνδέεστε.
            </h1>
            <p className="mt-5 max-w-2xl text-[15px] text-muted-foreground md:text-lg">
              Περισσότερα από μια ψηφιακή κάρτα. Μια ολοκληρωμένη πλατφόρμα δικτύωσης — NFC, εικονικές κάρτες, επαφές με AI
              και διαχείριση ομάδας. Όλα σε ένα.
            </p>
            <div className="mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
              <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Ξεκινήστε δωρεάν <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/shop" className="inline-flex items-center justify-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/80">
                Παραγγείλετε κάρτες NFC
              </Link>
            </div>
            <p className="mt-5 text-[12px] text-muted-foreground">
              Χωρίς πιστωτική κάρτα · Λειτουργεί σε iPhone & Android · Το αγαπούν 50.000+ επαγγελματίες
            </p>
          </div>

          <motion.div ref={heroVideoRef} style={{ scale: heroScale }} className="relative mt-10 origin-center md:mt-20">
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl bg-brand-dark">
              <video
                ref={heroVideoEl}
                className="aspect-video w-full object-cover rounded-none opacity-100"
                autoPlay
                loop
                muted
                playsInline
                poster={heroCard}
                preload="metadata"
                // @ts-expect-error fetchpriority is valid HTML but missing from React types
                fetchpriority="high"
              >
                <source src="/hero-video.webm" type="video/webm" />
                <track kind="captions" />
              </video>
              <button
                type="button"
                onClick={() => toggleMute(heroVideoEl, heroMuted, setHeroMuted)}
                aria-label={heroMuted ? "Ενεργοποίηση ήχου βίντεο" : "Σίγαση βίντεο"}
                className="absolute bottom-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-md transition hover:bg-black/75 md:bottom-5 md:right-5"
              >
                {heroMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* CARD SPECS */}
      <section className="bg-muted">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:gap-12 md:px-6 md:py-24">
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2rem] bg-foreground/5 blur-3xl" aria-hidden />
            <img src={nfcThin} alt="Κοντινή προβολή του chip NFC" loading="lazy" width={1600} height={1280} className="relative w-full rounded-2xl object-cover" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Με αντοχή στον χρόνο</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Απίστευτα λεπτή. <em className="italic">Εξαιρετικά ισχυρή.</em>
            </h2>
            <p className="mt-4 max-w-md text-[15px] text-muted-foreground md:text-base">
              Οι κάρτες NFC μας είναι λεπτότερες από μια πιστωτική κάρτα, αντέχουν στον χρόνο και ενσωματώνουν μια ολοκληρωμένη ψηφιακή ταυτότητα με ένα μόνο άγγιγμα.
            </p>
            <dl className="mt-7 grid grid-cols-2 gap-x-5 gap-y-4 md:mt-8 md:gap-x-6 md:gap-y-5">
              {SPECS.map((s) => (
                <div key={s.l} className="rounded-xl bg-card p-4">
                  <dt className="font-display text-lg leading-none">{s.v}</dt>
                  <dd className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.d}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>


      {/* PRODUCTS — One platform. Every way to connect. */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-10 md:px-6 md:pb-24 md:pt-14">
        <div className="mb-10 flex flex-col items-start gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Τα προϊόντα μας</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Μία πλατφόρμα.<br />Κάθε τρόπος σύνδεσης.
            </h2>
            <p className="mt-4 max-w-xl text-[15px] text-muted-foreground md:text-base">
              Είτε αγγίζετε, είτε σαρώνετε, είτε μοιράζεστε έναν σύνδεσμο — το banana.cards λειτουργεί όπου κι αν γνωρίζετε ανθρώπους.
            </p>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-bold underline-offset-4 hover:underline">
            Παραγγείλετε & εξατομικεύστε online <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {PRODUCTS.map((p) => (
            <Link key={p.title} to={p.href} className="group flex flex-col overflow-hidden rounded-2xl bg-muted transition">
              <div className="aspect-[5/4] overflow-hidden bg-card">
                <img src={p.img} alt={p.title} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
              </div>
              <div className="flex flex-1 flex-col p-6 md:p-7">
                <h3 className="font-display text-xl leading-tight md:text-3xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <ul className="mt-5 grid gap-2 text-sm">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /><span className="font-medium">{b}</span></li>
                  ))}
                </ul>
                <span className="mt-7 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* CARD VARIANTS — Your business card. Your way. */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-black text-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mb-10 flex flex-col items-start gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-background/60">Παραγγείλετε online</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
                Η επαγγελματική σας κάρτα. <em className="italic">Με τον δικό σας τρόπο.</em>
              </h2>
              <p className="mt-4 max-w-xl text-[15px] text-background/70 md:text-base">
                Σχεδιάστε, παραγγείλετε και παραλάβετε κορυφαίες κάρτες NFC και ψηφιακές κάρτες — πλήρως προσαρμοσμένες στην εταιρική σας ταυτότητα.
              </p>
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {CARD_VARIANTS.map((c) => (
              <div key={c.name} className="group flex flex-col overflow-hidden rounded-2xl bg-white/[0.06] transition hover:bg-white/[0.07]">
                <div className="aspect-[5/4] overflow-hidden bg-muted">
                  <img src={c.img} alt={c.name} loading="lazy" width={1200} height={960} className="h-full w-full object-cover transition group-hover:scale-[1.02]" />
                </div>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <h3 className="font-display text-xl font-bold leading-tight md:text-2xl">{c.name}</h3>
                  <p className="mt-2 text-sm text-background/65">{c.desc}</p>
                  <div className="mt-5 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
                    <p className="font-display text-2xl leading-none">
                      From {formatMoney(getProductBasePrice(c.slug, country.code), country)}
                    </p>
                    <Link to={c.href} className="inline-flex items-center gap-1.5 rounded-xl bg-brand-lime px-4 py-2 text-xs font-bold text-brand-lime-foreground transition hover:opacity-90">
                      Εξατομικεύστε <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-white/5">
              Δείτε όλα τα προϊόντα <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STEPS — From zero to connected in 3 steps */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 max-w-2xl md:mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Ξεκινήστε σε δευτερόλεπτα</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Από το μηδέν στη σύνδεση <em className="italic">σε 3 βήματα.</em>
          </h2>
        </div>
        <ol className="grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative flex flex-col rounded-2xl bg-muted p-6 md:p-8">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-foreground font-display text-xl font-bold text-background md:h-16 md:w-16 md:text-2xl">
                0{i + 1}
              </span>
              <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.tag}</p>
              <h3 className="mt-2 font-display text-2xl leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10 flex justify-center">
          <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-dark px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
            Ξεκινήστε δωρεάν <ArrowRight className="h-4 w-4" />
          </CtaLink>
        </div>
      </section>

      {/* SUSTAINABILITY */}
      <section className="relative -mt-8 overflow-hidden rounded-t-[2rem] bg-black text-background md:-mt-10 md:rounded-t-[3rem]">
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:gap-12 md:px-6 md:py-24">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-background/60">Βιωσιμότητα</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Καλό για εσάς. <br /> <em className="italic">Και για τον πλανήτη.</em>
            </h2>
            <p className="mt-4 max-w-md text-[15px] text-background/70 md:text-base">
              Κάθε κάρτα NFC banana.cards αντικαθιστά εκατοντάδες χάρτινες κάρτες στη διάρκεια ζωής της.
              Οι κάρτες μας κατασκευάζονται από ανακυκλωμένα υλικά ή υλικά βιώσιμης προέλευσης. Τέλος στις επανεκτυπώσεις. Τέλος στη σπατάλη.
            </p>
            <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6 md:mt-10 md:gap-6 md:pt-8">
              {[
                { v: "500+", l: "χάρτινες κάρτες εξοικονομούνται ανά κάρτα NFC" },
                { v: "100%", l: "αποστολή με ουδέτερο αποτύπωμα άνθρακα" },
                { v: "Οικολογικό",  l: "επιλογές ανακυκλωμένου PVC & μπαμπού" },
              ].map((s) => (
                <div key={s.l}>
                  <dt className="font-display text-2xl leading-none md:text-4xl">{s.v}</dt>
                  <dd className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-background/55 md:text-[11px]">{s.l}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="relative">
            <BeforeAfterSlider
              beforeSrc="/lovable-uploads/six-before.webp"
              afterSrc="/lovable-uploads/six-after.webp"
              beforeAlt="Παλιά χάρτινη επαγγελματική κάρτα"
              afterAlt="Κάρτα NFC banana.cards με ψηφιακό προφίλ"
              beforeLabel="Χαρτί"
              afterLabel="banana.cards"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <ReviewsSection />

      {/* PRICING TEASER */}
      <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mb-10 max-w-2xl md:mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Τιμές</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
              Απλές, διαφανείς τιμές. <em className="italic">Ξεκινήστε δωρεάν.</em>
            </h2>
            <p className="mt-4 text-[15px] text-muted-foreground md:text-base">
              Χωρίς κρυφές χρεώσεις. Χωρίς μακροχρόνια δέσμευση. Ξεκινήστε δωρεάν και εξελιχθείτε καθώς αναπτύσσεστε.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {PLANS.map((p) => {
              const isFree = p.planKey === "free";
              const paidKey = p.planKey === "pro_ai" || p.planKey === "teams" ? p.planKey : null;
              const priceText = isFree
                ? `${country.symbol}0`
                : paidKey
                  ? formatPlanPrice(country, getPlanMonthly(paidKey, country.code, "month"))
                  : defaultPlanPriceText("pro_ai", "month");
              const perLabel = p.planKey === "teams" ? "/ seat / month" : "/ month";
              return (
              <div key={p.name} className={`relative flex flex-col rounded-2xl bg-card p-7 md:p-8 ${p.featured ? "ring-2 ring-foreground" : ""}`}>
                {p.featured && (
                  <span className="absolute -top-3 left-6 inline-flex h-6 items-center justify-center rounded-xl bg-brand-lime px-3 text-[11px] font-bold text-brand-lime-foreground">
                    Δημοφιλέστερο
                  </span>
                )}
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{p.name}</p>
                <div className="mt-4 flex items-end gap-1.5">
                  <p className="font-display text-4xl leading-none md:text-5xl">{priceText}</p>
                  <p className="pb-1 text-sm text-muted-foreground">{perLabel}</p>
                </div>
                {!isFree && (
                  <span className="mt-1 inline-flex w-fit items-center rounded-full bg-brand-lime px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-brand-lime-foreground">
                    50% OFF Yearly
                  </span>
                )}
                <p className="mt-3 text-sm text-muted-foreground">{p.desc}</p>
                <CtaLink to={p.href} className={`mt-6 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition ${p.featured ? "bg-brand-dark text-background hover:opacity-90" : "bg-muted hover:bg-muted/70"}`}>
                  {p.cta} <ArrowRight className="h-4 w-4" />
                </CtaLink>
                <ul className="mt-8 grid gap-2.5 border-t border-foreground/10 pt-6 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0" /><span className="font-medium">{f}</span></li>
                  ))}
                </ul>
              </div>
              );
            })}
          </div>
          <p className="mt-8 text-center text-xs text-muted-foreground">
            Όλα τα προγράμματα περιλαμβάνουν δωρεάν δοκιμή 14 ημερών. Δεν απαιτείται πιστωτική κάρτα.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection />

      {/* BLOGS / RESOURCES */}
      <BlogsSection />

      {/* FINAL CTA — stacks onto the blogs section above */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="relative overflow-hidden rounded-3xl bg-brand-dark p-8 text-center text-background md:p-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_55%)]" aria-hidden />
          <img src={logoSvg} alt="" aria-hidden width={115} height={20} className="mx-auto h-5 w-auto brightness-0 invert" />
          <h2 className="mx-auto mt-5 max-w-3xl font-display text-3xl leading-[1.1] md:text-5xl">
            Αρχίστε να συνδέεστε <em className="italic text-brand-lime">διαφορετικά.</em>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-[15px] text-background/70 md:text-base">
            Γίνετε μέλος μαζί με 50.000+ επαγγελματίες που αναβάθμισαν τον τρόπο δικτύωσής τους. Η ρύθμιση διαρκεί 2 λεπτά.
          </p>
          <div className="relative mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
            <CtaLink to="/signup" className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
              Ξεκινήστε — δωρεάν
            </CtaLink>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-background/10">
              Μιλήστε με τις πωλήσεις
            </Link>
          </div>
        </div>
        </div>
      </section>

    </SiteLayout>
  );
}

function ReviewCard({ q, a, r }: { q: string; a: string; r: string }) {
  return (
    <figure className="flex flex-col rounded-2xl bg-muted p-5 md:p-6">
      <div className="flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="h-3.5 w-3.5 fill-foreground text-foreground" />
        ))}
      </div>
      <blockquote className="mt-3 text-[14px] leading-relaxed">"{q}"</blockquote>
      <figcaption className="mt-5 text-xs">
        <span className="font-bold">{a}</span> <span className="text-muted-foreground">— {r}</span>
      </figcaption>
    </figure>
  );
}

function ReviewsSection() {
  const all = [...REVIEWS, ...REVIEWS_EXTRA];
  const cols = [
    all.filter((_, i) => i % 3 === 0),
    all.filter((_, i) => i % 3 === 1),
    all.filter((_, i) => i % 3 === 2),
  ];
  const animClasses = ["animate-marquee-y", "animate-marquee-y-rev", "animate-marquee-y-slow"];
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
      <div className="mb-10 flex flex-col items-center gap-3 text-center md:mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Τι λένε οι πελάτες μας</p>
        <h2 className="font-display text-3xl leading-[1.1] md:text-5xl">
          Το αγαπούν 50.000+ επαγγελματίες <em className="italic">σε 170 χώρες.</em>
        </h2>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-foreground text-foreground" />
            ))}
          </div>
          <p className="text-sm font-bold">4.9 / 5 · 1.200+ αξιολογήσεις</p>
        </div>
      </div>
      <div
        className="relative grid h-[640px] grid-cols-1 gap-4 overflow-hidden md:grid-cols-3 md:gap-5"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)",
        }}
      >
        {cols.map((col, idx) => (
          <div key={idx} className={`flex flex-col gap-4 md:gap-5 ${animClasses[idx]}`}>
            {[...col, ...col].map((t, i) => (
              <ReviewCard key={`${idx}-${i}`} {...t} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-foreground/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-lg md:text-xl">{q}</span>
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-card">
          {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      {open && <p className="pb-5 pr-12 text-[15px] leading-relaxed text-muted-foreground">{a}</p>}
    </div>
  );
}

function FAQSection() {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-5xl px-4 py-16 md:px-6 md:py-24">
      <div className="mb-10 text-center md:mb-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Συχνές ερωτήσεις</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
          Έχετε ερωτήσεις; <em className="italic">Έχουμε απαντήσεις.</em>
        </h2>
      </div>
      <div className="mx-auto max-w-3xl rounded-3xl bg-muted px-6 md:px-10">
        {FAQS.map((f) => (
          <FAQItem key={f.q} {...f} />
        ))}
      </div>
      </div>
    </section>
  );
}

function BlogsSection() {
  return (
    <section className="relative -mt-8 rounded-t-[2rem] bg-muted md:-mt-10 md:rounded-t-[3rem]">
      <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mb-10 text-center md:mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Χρήσιμο υλικό</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-5xl">
            Άρθρα, χρήσιμο υλικό <em className="italic">και οδηγοί.</em>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-[15px] text-muted-foreground md:text-base">
            Σύντομες απαντήσεις στις συχνότερες ερωτήσεις για τις ψηφιακές επαγγελματικές κάρτες και τη σύγχρονη δικτύωση.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {BLOGS.map((b) => (
            <Link key={b.title} to={b.href} className="group flex flex-col overflow-hidden rounded-2xl bg-card transition">
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img src={b.img} alt={b.title} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-[1.03]" />
              </div>
              <div className="p-5 md:p-6">
                <h3 className="font-display text-lg leading-tight md:text-xl">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold underline-offset-4 group-hover:underline">
                  Διαβάστε το άρθρο <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
